import { useCallback, useEffect, useState } from 'react';
import type { Option, OptionValue, Product, Tenant } from '../../lib/database.types';
import { supabase } from '../../lib/supabase';
import { formatPrice } from '../../lib/cart';
import { uniqueSlug, duplicateOptionFields } from '../../lib/menuManager';
import { BOISSON_MENU_SLUG, MENU_DRINK_TRIGGER } from '../../lib/menuDrinks';
import {
  createOption,
  updateOption,
  deleteOption,
  addOptionValue,
  updateOptionValue,
  deleteOptionValue,
} from '../../lib/menuEditor';
import { seedDefaultOptions, DEFAULT_OPTIONS_LIBRARY } from '../../lib/defaultOptions';
import ConfirmDelete from './ConfirmDelete';

interface LibOption {
  option: Option;
  values: OptionValue[];
  productNames: string[];
}

/**
 * Sous-onglet « Bibliothèque d'options » (parité legacy OptionsTab).
 * Liste TOUTES les options réutilisables du resto (1 option = N produits affectés),
 * avec leurs valeurs, le type (choix unique/multi), requis/max, et le nombre de
 * produits qui l'utilisent. Création / édition / suppression.
 */
export default function OptionsLibrary({ tenant }: { tenant: Tenant }) {
  const [items, setItems] = useState<LibOption[]>([]);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [editing, setEditing] = useState<LibOption | null>(null);
  const [creating, setCreating] = useState(false);
  const [pendingDelete, setPendingDelete] = useState<{ label: string; run: () => void } | null>(null);
  const [seedMsg, setSeedMsg] = useState<string | null>(null);

  const reload = useCallback(async () => {
    const { data: opts } = await supabase.from('options').select('*').eq('tenant_id', tenant.id).order('position');
    const options = ((opts as unknown as Option[]) ?? []).sort((a, b) => a.position - b.position);
    const ids = options.map((o) => o.id);
    const [{ data: vals }, { data: links }, { data: prods }] = await Promise.all([
      ids.length ? supabase.from('option_values').select('*').in('option_id', ids) : Promise.resolve({ data: [] }),
      ids.length ? supabase.from('product_options').select('option_id, product_id').in('option_id', ids) : Promise.resolve({ data: [] }),
      supabase.from('products').select('id, name').eq('tenant_id', tenant.id),
    ]);
    const valuesByOption: Record<string, OptionValue[]> = {};
    for (const v of (vals as unknown as OptionValue[]) ?? []) (valuesByOption[v.option_id] ??= []).push(v);
    const nameById: Record<string, string> = {};
    for (const p of (prods as unknown as Product[]) ?? []) nameById[p.id] = p.name;
    const productsByOption: Record<string, string[]> = {};
    for (const l of (links as unknown as { option_id: string; product_id: string }[]) ?? []) {
      (productsByOption[l.option_id] ??= []).push(nameById[l.product_id] ?? '');
    }
    setItems(
      options.map((option) => ({
        option,
        values: (valuesByOption[option.id] ?? []).sort((a, b) => a.position - b.position),
        productNames: (productsByOption[option.id] ?? []).filter(Boolean),
      })),
    );
    setLoading(false);
  }, [tenant.id]);

  useEffect(() => {
    reload();
  }, [reload]);

  const run = (fn: () => Promise<unknown>) => async () => {
    setBusy(true);
    try {
      await fn();
      await reload();
    } finally {
      setBusy(false);
    }
  };

  /** Duplique une option (copie type/valeurs/tarif/dépendance) → nouvelle option indépendante. */
  const duplicate = (li: LibOption) =>
    run(async () => {
      const fields = duplicateOptionFields(
        li.option,
        items.map((i) => i.option.slug),
      );
      const created = await createOption(tenant.id, fields);
      if (!created) return;
      let pos = 0;
      for (const v of li.values) {
        await addOptionValue(created.id, {
          name: v.name,
          price_delta_cents: v.price_delta_cents ?? 0,
          is_default: !!v.is_default,
          is_available: v.is_available ?? true,
          position: pos++,
        });
      }
    })();

  /** Charge la bibliothèque d'options par défaut (idempotent : ne fait rien si déjà remplie). */
  const loadDefaults = async () => {
    setBusy(true);
    setSeedMsg(null);
    try {
      const { created } = await seedDefaultOptions(tenant.id);
      setSeedMsg(
        created > 0
          ? `✅ ${created} groupe(s) d'options ajouté(s). Affectez-les à vos produits depuis l'onglet Produits.`
          : 'ℹ️ Bibliothèque déjà à jour : aucun nouveau groupe à ajouter.',
      );
      await reload();
    } finally {
      setBusy(false);
    }
  };

  if (loading) return <p className="opacity-70">Chargement des options…</p>;

  return (
    <div>
      <div className="mb-3 flex items-center justify-between">
        <div>
          <h3 className="text-base font-bold">Bibliothèque d'options réutilisables</h3>
          <p className="text-sm text-slate-500">
            {items.length} option{items.length > 1 ? 's' : ''} · 1 option = N produits affectés
          </p>
        </div>
        <div className="flex flex-wrap gap-2">
          <button
            onClick={loadDefaults}
            disabled={busy}
            className="rounded-lg border border-brand-primary px-3 py-2 text-sm font-semibold text-brand-primary hover:bg-brand-light disabled:opacity-50"
            title={`Ajoute ${DEFAULT_OPTIONS_LIBRARY.length} groupes prêts à l'emploi (sauces, crudités, viandes, suppléments, boissons, desserts…)`}
          >
            ✨ Charger la bibliothèque par défaut
          </button>
          <button
            onClick={() => {
              setCreating(true);
              setEditing({
                option: { id: '', tenant_id: tenant.id, name: '', slug: '', selection: 'single', is_required: false, min_choices: 0, max_choices: 1, position: items.length, pricing_mode: 'flat', extra_each_cents: 0 } as Option,
                values: [],
                productNames: [],
              });
            }}
            className="rounded-lg px-3 py-2 text-sm font-semibold text-white"
            style={{ background: 'var(--brand-primary)' }}
          >
            + Nouvelle option
          </button>
        </div>
      </div>

      <p className="mb-2 rounded-lg bg-amber-50 p-3 text-sm text-amber-900">
        💡 Astuce : créez une seule fois « Choix sauces », puis attachez-la à plusieurs produits depuis l'onglet Produits.
      </p>
      {seedMsg && <p className="mb-4 rounded-lg bg-slate-50 p-3 text-sm">{seedMsg}</p>}

      <div className="grid gap-4 lg:grid-cols-2">
        {items.map(({ option, values, productNames }) => (
          <div key={option.id} className="rounded-xl border border-slate-200 bg-white p-4">
            <div className="flex items-start justify-between">
              <div>
                <div className="text-base font-extrabold">{option.name}</div>
                <div className="mt-1 flex flex-wrap gap-1 text-xs">
                  <span className="rounded bg-slate-100 px-1.5 py-0.5">
                    {option.selection === 'multiple' ? 'Multi-choix' : 'Choix unique'}
                  </span>
                  {option.is_required && <span className="rounded bg-red-50 px-1.5 py-0.5 text-red-700">Requis</span>}
                  {option.selection === 'multiple' && option.max_choices != null && (
                    <span className="rounded bg-slate-100 px-1.5 py-0.5">Max {option.max_choices}</span>
                  )}
                  {option.slug === BOISSON_MENU_SLUG && (
                    <span className="rounded bg-sky-50 px-1.5 py-0.5 text-sky-700">🥤 Boisson au menu (auto)</span>
                  )}
                </div>
              </div>
              <div className="flex gap-1">
                <button
                  onClick={() => {
                    setCreating(false);
                    setEditing({ option, values, productNames });
                  }}
                  className="rounded-md bg-slate-100 px-2 py-1 text-xs font-semibold hover:bg-slate-200"
                >
                  Éditer
                </button>
                <button
                  onClick={() => duplicate({ option, values, productNames })}
                  disabled={busy}
                  className="rounded-md bg-slate-100 px-2 py-1 text-xs font-semibold hover:bg-slate-200 disabled:opacity-50"
                  title="Dupliquer cette option"
                >
                  Dupliquer
                </button>
                <button
                  onClick={() => setPendingDelete({ label: `l'option « ${option.name} »`, run: run(() => deleteOption(option.id)) })}
                  className="text-slate-400 hover:text-red-600"
                  title="Supprimer"
                >
                  🗑️
                </button>
              </div>
            </div>
            <ul className="mt-2 text-sm text-slate-600">
              {values.slice(0, 4).map((v) => (
                <li key={v.id} className="flex justify-between">
                  <span>{v.name}</span>
                  {(v.price_delta_cents ?? 0) > 0 && <span>+{formatPrice(v.price_delta_cents ?? 0, tenant.currency)}</span>}
                </li>
              ))}
              {values.length > 4 && <li className="opacity-50">+{values.length - 4} autre(s)…</li>}
            </ul>
            {productNames.length > 0 && (
              <div className="mt-2 truncate text-xs text-slate-400">Affectée à {productNames.length} produit(s) : {productNames.join(', ')}</div>
            )}
          </div>
        ))}
        {items.length === 0 && <p className="text-sm opacity-60">Aucune option. Cliquez « + Nouvelle option ».</p>}
      </div>

      {editing && (
        <OptionModal
          item={editing}
          isNew={creating}
          tenant={tenant}
          busy={busy}
          onCancel={() => setEditing(null)}
          onSaved={async () => {
            setEditing(null);
            await reload();
          }}
          setBusy={setBusy}
          allSlugs={items.map((i) => i.option.slug)}
        />
      )}

      <ConfirmDelete
        open={!!pendingDelete}
        label={pendingDelete?.label ?? ''}
        onCancel={() => setPendingDelete(null)}
        onConfirm={() => {
          pendingDelete?.run();
          setPendingDelete(null);
        }}
      />
    </div>
  );
}

/** Modale d'édition d'une option + ses valeurs (création ou édition). */
function OptionModal({
  item,
  isNew,
  tenant,
  busy,
  onCancel,
  onSaved,
  setBusy,
  allSlugs,
}: {
  item: LibOption;
  isNew: boolean;
  tenant: Tenant;
  busy: boolean;
  onCancel: () => void;
  onSaved: () => void;
  setBusy: (b: boolean) => void;
  allSlugs: string[];
}) {
  const [name, setName] = useState(item.option.name);
  const [selection, setSelection] = useState<'single' | 'multiple'>(item.option.selection === 'multiple' ? 'multiple' : 'single');
  const [required, setRequired] = useState(!!item.option.is_required);
  const [maxChoices, setMaxChoices] = useState(item.option.max_choices ?? 1);
  // « Boisson incluse au menu » : valeurs auto (catégorie Boissons), s'affiche quand un format contenant « boisson » est choisi.
  const [menuDrink, setMenuDrink] = useState(item.option.slug === BOISSON_MENU_SLUG);
  const [values, setValues] = useState<{ id?: string; name: string; delta: number }[]>(
    item.values.map((v) => ({ id: v.id, name: v.name, delta: (v.price_delta_cents ?? 0) / 100 })),
  );

  const save = async () => {
    setBusy(true);
    try {
      let optionId = item.option.id;
      const patch: Partial<Option> = {
        name: name.trim(),
        selection: menuDrink ? 'single' : selection,
        is_required: required,
        min_choices: required ? 1 : 0,
        max_choices: menuDrink ? 1 : selection === 'multiple' ? maxChoices : 1,
      };
      const wasMenuDrink = item.option.slug === BOISSON_MENU_SLUG;
      if (menuDrink) {
        // Marqueur : slug conventionnel + déclencheur « boisson » (tout groupe parent).
        patch.slug = BOISSON_MENU_SLUG;
        patch.depends_on_option_slug = null;
        patch.depends_on_value = MENU_DRINK_TRIGGER;
      } else if (wasMenuDrink) {
        // On retire le caractère « boisson au menu » → slug neutre + plus de dépendance.
        patch.slug = uniqueSlug(name, allSlugs);
        patch.depends_on_option_slug = null;
        patch.depends_on_value = null;
      }
      if (isNew) {
        const created = await createOption(tenant.id, {
          name: name.trim(),
          slug: menuDrink ? BOISSON_MENU_SLUG : uniqueSlug(name, allSlugs),
          ...patch,
        });
        optionId = created?.id ?? '';
      } else {
        await updateOption(optionId, patch);
      }
      if (!optionId) return;
      // Boisson au menu : les valeurs sont automatiques (catégorie Boissons) → on n'écrit rien.
      if (!menuDrink) {
        // Valeurs : maj/insert, et suppression de celles retirées.
        const keptIds = new Set(values.filter((v) => v.id).map((v) => v.id));
        for (const orig of item.values) {
          if (!keptIds.has(orig.id)) await deleteOptionValue(orig.id);
        }
        let pos = 0;
        for (const v of values) {
          const cents = Math.round((v.delta || 0) * 100);
          if (v.id) await updateOptionValue(v.id, { name: v.name, price_delta_cents: cents, position: pos });
          else await addOptionValue(optionId, { name: v.name, price_delta_cents: cents, position: pos });
          pos += 1;
        }
      }
      onSaved();
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center overflow-y-auto bg-black/40 p-4">
      <div className="my-8 w-full max-w-lg rounded-2xl bg-white p-5">
        <h3 className="mb-3 text-lg font-bold">{isNew ? 'Nouvelle option' : "Modifier l'option"}</h3>

        <label className="mb-1 block text-xs font-semibold opacity-60">Nom de l'option</label>
        <input value={name} onChange={(e) => setName(e.target.value)} placeholder="Ex. : Choix sauces" className="mb-3 w-full rounded-lg border border-slate-200 p-2" />

        <label className="mb-3 flex items-start gap-2 rounded-lg border border-sky-200 bg-sky-50 p-2.5 text-sm">
          <input type="checkbox" checked={menuDrink} onChange={(e) => setMenuDrink(e.target.checked)} className="mt-0.5" />
          <span>
            <span className="font-semibold">🥤 Boisson incluse au menu (auto)</span>
            <span className="mt-0.5 block text-xs text-sky-800/80">
              Se remplit automatiquement avec les boissons de la catégorie « Boissons » du magasin, et s'affiche pour le client dès qu'un format contenant « boisson » est choisi (ex. « Boisson », « Boisson et frite »).
            </span>
          </span>
        </label>

        <div className="mb-3 grid grid-cols-2 gap-3">
          <div>
            <label className="mb-1 block text-xs font-semibold opacity-60">Type</label>
            <select value={menuDrink ? 'single' : selection} disabled={menuDrink} onChange={(e) => setSelection(e.target.value as 'single' | 'multiple')} className="w-full rounded-lg border border-slate-200 p-2 disabled:bg-slate-50">
              <option value="single">Choix unique</option>
              <option value="multiple">Multi-choix</option>
            </select>
          </div>
          <div>
            <label className="mb-1 block text-xs font-semibold opacity-60">Max sélections {(menuDrink || selection === 'single') && '(N/A)'}</label>
            <input type="number" min={1} value={maxChoices} disabled={menuDrink || selection === 'single'} onChange={(e) => setMaxChoices(parseInt(e.target.value || '1', 10))} className="w-full rounded-lg border border-slate-200 p-2 disabled:bg-slate-50" />
          </div>
        </div>

        <label className="mb-3 flex items-center gap-2 text-sm">
          <input type="checkbox" checked={required} onChange={(e) => setRequired(e.target.checked)} />
          <span className="font-semibold">Obligatoire</span>
        </label>

        {menuDrink ? (
          <div className="mb-3 rounded-lg bg-slate-50 p-3 text-sm text-slate-600">
            Les boissons proposées sont <strong>gérées automatiquement</strong> : ce sont les produits disponibles de votre catégorie « Boissons ». Pour changer la liste, ajoutez/retirez des boissons dans l'onglet <strong>Produits</strong>.
          </div>
        ) : (
        <div className="mb-3">
          <div className="mb-1 flex items-center justify-between">
            <label className="text-xs font-semibold opacity-60">Valeurs disponibles</label>
            <button onClick={() => setValues((v) => [...v, { name: '', delta: 0 }])} className="text-xs font-semibold" style={{ color: 'var(--brand-primary)' }}>
              + Ajouter une valeur
            </button>
          </div>
          <ul className="flex flex-col gap-1.5">
            {values.map((v, i) => (
              <li key={i} className="flex items-center gap-2">
                <input
                  value={v.name}
                  onChange={(e) => setValues((arr) => arr.map((x, j) => (j === i ? { ...x, name: e.target.value } : x)))}
                  placeholder="Label (ex. : Algérienne)"
                  className="flex-1 rounded border border-slate-200 p-1.5 text-sm"
                />
                <span className="text-xs opacity-50">+</span>
                <input
                  type="number"
                  step="0.10"
                  value={v.delta}
                  onChange={(e) => setValues((arr) => arr.map((x, j) => (j === i ? { ...x, delta: parseFloat(e.target.value || '0') } : x)))}
                  className="w-16 rounded border border-slate-200 p-1.5 text-right text-sm"
                />
                <button onClick={() => setValues((arr) => arr.filter((_, j) => j !== i))} className="text-slate-300 hover:text-red-600" title="Retirer">✕</button>
              </li>
            ))}
            {values.length === 0 && <li className="text-xs opacity-50">Aucune valeur. Cliquez « + Ajouter une valeur ».</li>}
          </ul>
        </div>
        )}

        <div className="flex justify-end gap-2">
          <button onClick={onCancel} className="rounded-lg border border-slate-300 px-4 py-2 text-sm font-semibold">Annuler</button>
          <button onClick={save} disabled={busy || !name.trim()} className="rounded-lg px-4 py-2 text-sm font-semibold text-white disabled:opacity-40" style={{ background: 'var(--brand-primary)' }}>
            {isNew ? 'Créer' : 'Enregistrer'}
          </button>
        </div>
      </div>
    </div>
  );
}
